import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Play, BookOpen, Zap, CheckCircle, ArrowRight, Code } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WORKFLOW_TEMPLATES } from '@/services/templates';

const TEMPLATES_LIST = Object.values(WORKFLOW_TEMPLATES);

export function PrebuiltTemplateShowcase() {
  const { importWorkflow } = useWorkflowStore();
  const [selectedTemplate, setSelectedTemplate] = useState<typeof TEMPLATES_LIST[0] | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const handleCloneTemplate = (template: typeof TEMPLATES_LIST[0]) => {
    const { difficulty, category, features, useCase, ...workflow } = template as typeof template & {
      useCase?: string;
    };
    void difficulty;
    void category;
    void features;
    void useCase;

    importWorkflow({
      ...workflow,
      name: `${template.name} (Copy)`,
      nodes: template.nodes.map((node) => ({ ...node })),
      edges: template.edges.map((edge) => ({ ...edge })),
    });

    toast.success(`Created "${template.name} (Copy)" — ready to customize!`);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'Intermediate':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'Advanced':
        return 'bg-purple-500/10 text-purple-700 border-purple-500/20';
      case 'Expert':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-muted';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return '🌱';
      case 'Intermediate':
        return '📈';
      case 'Advanced':
        return '🚀';
      case 'Expert':
        return '⚡';
      default:
        return '•';
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-3 pb-4 md:pb-6">
        <h1 className="text-2xl md:text-4xl font-bold text-foreground">Pre-Built Workflow Templates</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Learn by example. Clone any of these production-ready workflows, customize them for your needs, and master the system's powerful capabilities.
        </p>
      </div>

      {/* Tabs for Different Views */}
      <Tabs defaultValue="gallery" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="gallery">Gallery View</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
          <TabsTrigger value="learning">Learning Path</TabsTrigger>
        </TabsList>

        {/* Gallery View */}
        <TabsContent value="gallery" className="space-y-6">
          <div className="grid gap-6">
            {TEMPLATES_LIST.map((template, idx) => (
              <Card
                key={template.id}
                className="p-6 border-2 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="grid md:grid-cols-[1fr_auto] gap-6">
                  {/* Left Content */}
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-3xl">{template.name.split(' ')[0]}</span>
                          <h2 className="text-2xl font-bold text-foreground">
                            {template.name.substring(2)}
                          </h2>
                        </div>
                        <p className="text-muted-foreground text-sm mb-3">
                          {template.description}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <Badge className={getDifficultyColor(template.difficulty)}>
                          {getDifficultyIcon(template.difficulty)} {template.difficulty}
                        </Badge>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">Key Features:</p>
                      <div className="flex flex-wrap gap-2">
                        {(template.features || []).map((feature) => (
                          <Badge key={feature} variant="outline" className="text-xs">
                            ✓ {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Node Count & Complexity */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {template.nodes?.length || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Nodes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {template.edges?.length || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Connections</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {Math.ceil((template.nodes?.length || 0) / 5)}
                        </p>
                        <p className="text-xs text-muted-foreground">Steps</p>
                      </div>
                    </div>

                    {/* Use Cases */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">Ideal for:</p>
                      <p className="text-sm text-muted-foreground">
                        {'useCase' in template && template.useCase
                          ? template.useCase
                          : template.description}
                      </p>
                    </div>
                  </div>

                  {/* Right Actions */}
                  <div className="flex flex-col gap-3 justify-start">
                    <Button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowDetailsDialog(true);
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Learn
                    </Button>
                    <Button
                      onClick={() => handleCloneTemplate(template)}
                      className="w-full"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Clone & Edit
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowDetailsDialog(true);
                      }}
                    >
                      <Code className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Comparison View */}
        <TabsContent value="comparison" className="space-y-6">
          <Card className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold">Template</th>
                  <th className="text-center py-3 px-4 font-semibold">Difficulty</th>
                  <th className="text-center py-3 px-4 font-semibold">Nodes</th>
                  <th className="text-center py-3 px-4 font-semibold">Connections</th>
                  <th className="text-left py-3 px-4 font-semibold">Category</th>
                  <th className="text-center py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {TEMPLATES_LIST.map((template) => (
                  <tr key={template.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-foreground">{template.name}</p>
                        <p className="text-xs text-muted-foreground">{template.description}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={getDifficultyColor(template.difficulty)}>
                        {template.difficulty}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center font-semibold">
                      {template.nodes?.length || 0}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold">
                      {template.edges?.length || 0}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline">{template.category}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button
                        size="sm"
                        onClick={() => handleCloneTemplate(template)}
                      >
                        Clone
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Learning Path */}
        <TabsContent value="learning" className="space-y-6">
          <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-blue-500/5 border-blue-500/20">
            <h3 className="text-lg font-semibold text-foreground mb-4">Recommended Learning Path</h3>
            <div className="space-y-4">
              {TEMPLATES_LIST.map((template, idx) => (
                <div key={template.id} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center font-semibold text-primary">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground mb-1">{template.name}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getDifficultyColor(template.difficulty)}>
                        {template.difficulty}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        Est. time: {idx === 0 ? '10 min' : idx === 1 ? '20 min' : idx === 2 ? '30 min' : '45 min'}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {idx === 0 && 'Start here! Learn the basics of triggers, transformations, and actions.'}
                      {idx === 1 && 'Master conditional logic and data validation patterns.'}
                      {idx === 2 && 'Explore multi-step workflows with error handling and API orchestration.'}
                      {idx === 3 && 'Build production-ready pipelines with enterprise integrations.'}
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground">Learning Objectives:</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {(template.features || []).slice(0, 3).map((feature) => (
                          <li key={feature} className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleCloneTemplate(template)}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Start {idx === 0 ? 'Learning' : 'Next Level'}
                      </Button>
                    </div>
                  </div>
                  {idx < TEMPLATES_LIST.length - 1 && (
                    <ArrowRight className="w-5 h-5 text-muted-foreground mt-1" />
                  )}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Tips */}
      <Card className="p-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Getting Started Tips
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">1. Clone a Template</p>
            <p className="text-sm text-muted-foreground">
              Click "Clone & Edit" to create your own copy of any template.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">2. Explore the Nodes</p>
            <p className="text-sm text-muted-foreground">
              Study how nodes are connected and configured in each workflow.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">3. Modify & Experiment</p>
            <p className="text-sm text-muted-foreground">
              Change configurations, add nodes, or create new connections safely.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">4. Test & Monitor</p>
            <p className="text-sm text-muted-foreground">
              Run your workflow and check the execution results and logs.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">5. Save Versions</p>
            <p className="text-sm text-muted-foreground">
              Save versions before making major changes for easy rollback.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">6. Share & Collaborate</p>
            <p className="text-sm text-muted-foreground">
              Share workflows with team members and collect feedback.
            </p>
          </div>
        </div>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-96 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Workflow Architecture</h4>
                <div className="p-4 bg-muted/50 rounded-lg text-sm font-mono">
                  <div className="space-y-1">
                    <p>Total Nodes: <span className="font-bold">{selectedTemplate.nodes?.length}</span></p>
                    <p>Total Connections: <span className="font-bold">{selectedTemplate.edges?.length}</span></p>
                    <p>
                      Node Types: {' '}
                      <span className="font-bold">
                        {[
                          ...new Set(selectedTemplate.nodes?.map(n => n.subtype) || []),
                        ].join(', ')}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Key Features</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedTemplate.features || []).map((feature) => (
                    <Badge key={feature} variant="outline">
                      ✓ {feature}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">What You'll Learn</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    How to use {selectedTemplate.nodes?.map(n => n.subtype).filter((v, i, a) => a.indexOf(v) === i).join(', ')} nodes
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    Building {selectedTemplate.edges && selectedTemplate.edges.length > 3 ? 'complex' : 'simple'} workflow connections
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    Data transformation and processing patterns
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    Error handling and recovery strategies
                  </li>
                </ul>
              </div>

              <Button
                onClick={() => {
                  handleCloneTemplate(selectedTemplate);
                  setShowDetailsDialog(false);
                }}
                className="w-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Clone This Template Now
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}